import { fetchJson, csrfFetch } from "./fetch";
export { errorFn } from "./fetch";

export function fetchJsonApi(path, options = {}) {
  const myOptions = Object.assign({}, options);
  myOptions.headers = new Headers(options.headers || {});
  if (options.method && options.method.toUpperCase() !== "GET") {
    myOptions.headers.set("Content-Type", "application/vnd.api+json");
  }
  myOptions.headers.set("Accept", "application/vnd.api+json, application/json");
  if (myOptions.method == "DELETE") return csrfFetch(path, myOptions);
  return fetchJson(path, myOptions);
}

// If at least one value is a string, cast both to strings (null and undefined as
// "" instead of "null" and "undefined") and make lower-case. Else-if either is a
// number, cast both to numbers and replace any cast failures with zero.
// Not very efficient since it has to infer argument types each time it's called.
function sortableAttributes(a, b, key) {
  const [av, bv] = [a.attributes[key], b.attributes[key]]
  if (typeof av === "string" || typeof bv === "string" || av instanceof String || bv instanceof String) {
    return [(av?.toString()||"").toLowerCase(), (bv?.toString()||"").toLowerCase()]
  }
  if (typeof av === "number" || typeof bv === "number") {
    return [Number(av) || 0, Number(bv) || 0]
  }
  return [av, bv];
}

export function makeSortFn(jsonApiSort) {
  const sortFns = jsonApiSort?.split(',').
    map(key => key[0] === "-" ? [key.substring(1), -1] : [key, 1]).
    map(([key, dir]) => {
      if (key === "id") return (a, b) => dir * (Number(a.id) - Number(b.id));
      return (a, b) => {
        const [av, bv] = sortableAttributes(a, b, key);
        return dir * ((av < bv) ? -1 : (av == bv) ? 0 : 1)
      }
    }) || [];
  return (a, b) => {
    for (const fn of sortFns) {
      const result = fn(a, b);
      if (result !== 0) return result;
    }
    return 0;
  }
}

function makeDestroyFn(url, wrapped, collection, included) {
  return async function () {
    const response = await fetchJsonApi(url, { method: "DELETE" });
    if (!response.ok) {
      const error = await response.json().then(
        json => { throw json },
        _ => { throw `#{response.status} #{response.statusText}` }
      )
      throw error;
    }
    if (collection) {
      collection.delete({ id: wrapped.id, type: wrapped.type }, included);
    }
    return response
  }
}

let nullCollectionWrapRecord

function makeUpdateFn(url, wrapped, collection, included, method = "PATCH") {
  return async function (toUpdate = {}, options = null) {
    const { relationships, ...attributes } = toUpdate;
    const data = {
      id: wrapped.id,
      type: options?.type || wrapped.type,
      attributes
    };
    if (relationships) {
      const rels = { ...relationships }; // Don't mutate object passed in
      // Move value under boilerplate data: key if it isn't already
      for (const key of Object.keys(rels)) {
        const value = rels[key];
        if (!value.data) rels[key] = { data: value };
      }
      data.relationships = rels;
    }
    const result = await fetchJsonApi(url, {
      method,
      body: JSON.stringify({ data })
    });
    if (result.errors) throw result;
    if (options?.raw) return result;
    if (!collection && result.data) {
      if (!nullCollectionWrapRecord) nullCollectionWrapRecord = wrapRecordFn(null);
      return nullCollectionWrapRecord(result.data);
    }
    const updated = collection?.addResult(result, included);
    if (updated) return updated;
    throw result;
  }
}

export function wrapRecordFn(collection) {
  const alwaysProps = { id: true, type: true }
  const selfActionProps = { destroy: true, update: true };
  return function (record, included = false) {
    return new Proxy(record, {
      get: function (target, prop, receiver) {
        // Core metadata
        if (prop == "id" || prop == "type" || prop == "links") return target[prop];
        // Attributes
        if (target.attributes && prop in target.attributes) return target.attributes[prop];
        // Actions
        if (target.links) {
          if (prop == "destroy" && target.links.self) return makeDestroyFn(target.links.self, receiver, collection, included);
          if (prop == "update" && target.links.self) return makeUpdateFn(target.links.self, receiver, collection, included);
          if (target.links[`action_${prop}`]) return makeUpdateFn(target.links[`action_${prop}`], receiver, collection, included, "POST");
        }
        // Relations
        if (target.relationships && collection) {
          // We cannot yet fetch relationships, so collection must be present (enforced)
          // and its `data` or `included` must include the related record(s) (not enforced).
          if (target.relationships[prop]) return relationshipFunction(collection, target.relationships[prop]);
        }
      },
      has: function (target, prop) {
        if (prop in alwaysProps) return true;
        if (prop == "links" && target.links) return true;
        if (target.attributes && prop in target.attributes) return true;
        if (prop in selfActionProps && target.links?.self) return true;
        if (target.links?.[`action_${prop}`]) return true;
        if (collection && target.relationships?.[prop]) return true;
        return false;
      },
      ownKeys: function (target) {
        const selfActions = target.links?.self ? Object.keys(selfActionProps) : [];
        const linksProp = target.links ? ["links"] : [];
        const actions = target.links ? Object.entries(target.links).filter(([key, value]) => value && key.startsWith("action_")).map(([key, _]) => key.substring(7)) : [];
        const relationships = (target.relationships && collection) ? Reflect.ownKeys(target.relationships) : [];
        return [...Object.keys(alwaysProps), ...linksProp, ...Reflect.ownKeys(target.attributes || {}), ...selfActions, ...actions, ...relationships];
      }
    });
  }
}

function relationshipFunction(collection, relationship) {
  return function () {
    const data = relationship.data;
    if (!data) return data;
    if (Array.isArray(data)) return data.map(identifier => { return collection.get(identifier) || identifier });
    return collection.get(data) || data;
  }
}